import { Command, HelpCircle, Menu, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { useCommandPalette } from "@/components/intel/CommandPalette";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { SidebarNav } from "@/components/intel/SidebarNav";
import { startOnboardingTour } from "@/components/intel/OnboardingTour";
import { NotificationsPopover } from "@/components/intel/NotificationsPopover";

export function Topbar({ caseId = "INV-2025-0419" }: { caseId?: string }) {
  const [now, setNow] = useState<string>("");
  const [query, setQuery] = useState("");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const open = useCommandPalette();
  useEffect(() => {
    const tick = () => setNow(new Date().toUTCString().slice(17, 25));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);
  return (
    <header className="flex h-12 shrink-0 items-center gap-2 border-b border-panel-border bg-panel/60 px-3 sm:px-4 backdrop-blur">
      <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
        <SheetTrigger asChild>
          <button
            aria-label="Open navigation"
            aria-expanded={mobileNavOpen}
            aria-controls="mobile-navigation"
            className="lg:hidden inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-panel-border bg-background/40 text-muted-foreground hover:text-foreground"
          >
            <Menu className="h-4 w-4" />
          </button>
        </SheetTrigger>
        <SheetContent side="left" className="w-[min(18rem,86vw)] p-0 bg-panel/95 backdrop-blur border-panel-border overflow-y-auto">
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <div id="mobile-navigation" className="h-full">
            <SidebarNav
              onNavigate={() => {
                // Defer close until after TanStack Router has applied the
                // navigation — closing inside the same click that triggers
                // a Link can race with Radix Dialog's focus-trap teardown
                // and swallow the navigation on mobile.
                setTimeout(() => setMobileNavOpen(false), 0);
              }}
            />
          </div>
        </SheetContent>
      </Sheet>

      <div className="hidden md:block font-mono text-[11px] text-muted-foreground truncate">
        CASE <span className="text-foreground">{caseId}</span>
        <span className="mx-2 text-panel-border">|</span>
        STATUS <span className="text-success">ACTIVE</span>
        <span className="mx-2 text-panel-border">|</span>
        CLEARANCE <span className="text-amber">RESTRICTED</span>
      </div>
      <div className="md:hidden min-w-0 font-mono text-[10px] text-muted-foreground truncate">
        <span className="text-foreground">{caseId}</span>
        <span className="mx-1.5 text-panel-border">·</span>
        <span className="text-success">ACTIVE</span>
      </div>

      <div className="ml-auto flex shrink-0 items-center gap-1 sm:gap-3">
        <form
          onSubmit={(e) => { e.preventDefault(); open(query); }}
          className="hidden sm:flex items-center gap-2 rounded-md border border-panel-border bg-background/40 px-2.5 py-1.5 text-xs text-muted-foreground focus-within:border-cyan/50"
        >
          <Search className="h-3.5 w-3.5" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => { if (!query) open(""); }}
            placeholder="Search anything…"
            className="w-28 md:w-36 bg-transparent outline-none placeholder:text-muted-foreground"
          />
          <span className="hidden md:flex ml-3 items-center gap-1 font-mono text-[10px] text-muted-foreground">
            <Command className="h-3 w-3" /> K
          </span>
        </form>
        <button
          aria-label="Search"
          onClick={() => open("")}
          className="sm:hidden inline-flex h-9 w-9 items-center justify-center rounded-md border border-panel-border bg-background/40 text-muted-foreground hover:text-foreground"
        >
          <Search className="h-4 w-4" />
        </button>
        <NotificationsPopover />
        <button
          aria-label="Replay onboarding tour"
          onClick={() => startOnboardingTour()}
          title="Replay onboarding tour"
          className="inline-flex h-9 items-center justify-center gap-1.5 rounded-md border border-panel-border bg-background/40 px-2 text-muted-foreground hover:text-foreground"
        >
          <HelpCircle className="h-4 w-4" />
          <span className="hidden md:inline text-xs">Tour</span>
        </button>
        <div className="hidden md:block font-mono text-[11px] text-muted-foreground" suppressHydrationWarning>
          {now || "--:--:--"} <span className="text-cyan">UTC</span>
        </div>
      </div>
    </header>
  );
}
